import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Calculators", href: "/calculators" },
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Terms & Conditions", href: "/terms-and-conditions" },
  { label: "Disclaimer", href: "/disclaimer" },
  { label: "Cookie Policy", href: "/cookie-policy" },
  { label: "Grievance Redressal", href: "/grievance-redressal" },
];

export default function PageHeader() {
  const { pathname } = useLocation();

  const [open, setOpen] = useState(false);

  const mobileMenuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);

  // Keep the existing header structure for all calculator pages,
  // including nested routes such as /calculators/emi-calculator.
  const isCalculatorPage =
    pathname === "/calculators" || pathname.startsWith("/calculators/");

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close the mobile menu with Escape.
  useEffect(() => {
    if (!open) {
      return;
    }

    const handler = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handler);

    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Focus the first item when the menu opens and keep Tab navigation
  // inside the mobile navigation.
  useEffect(() => {
    if (!open) {
      return;
    }

    firstFocusableRef.current?.focus();

    const handleTab = (event) => {
      if (event.key !== "Tab") {
        return;
      }

      const first = firstFocusableRef.current;
      const last = lastFocusableRef.current;

      if (!first || !last) {
        return;
      }

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleTab);

    return () => document.removeEventListener("keydown", handleTab);
  }, [open]);

  // Close the mobile menu when clicking outside it.
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleClickOutside = (event) => {
      const clickedInsideMenu = mobileMenuRef.current?.contains(event.target);
      const clickedMenuButton = menuButtonRef.current?.contains(event.target);

      if (!clickedInsideMenu && !clickedMenuButton) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-gray-200">
      <div
        className="max-w-7xl mx-auto min-h-16 h-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between"
      >
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center shrink-0"
          data-testid="page-header-logo"
          onClick={() => setOpen(false)}
        >
          <img
            src="/Logo.webp"
            alt="Money Mart"
            className="h-[60px] w-auto object-contain"
          />
        </Link>

        {/* Calculator pages: preserve the existing Home button */}
        {isCalculatorPage ? (
          <Link
            to="/"
            data-testid="page-header-home"
            onClick={() => setOpen(false)}
          >
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full text-sm"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Home
            </Button>
          </Link>
        ) : (
          <>
            {/* Desktop navigation */}
            <nav
              className="hidden lg:flex flex-1 min-w-0 items-center justify-end flex-wrap gap-1 sm:gap-2 py-2"
              aria-label="Main navigation"
            >
              {NAV_LINKS.map((link) => {
                const isActive =
                  link.href === "/"
                    ? pathname === "/"
                    : pathname === link.href ||
                      pathname.startsWith(`${link.href}/`);

                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setOpen(false)}
                    className={`px-2 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                      isActive
                        ? "bg-gray-100 text-gray-900"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>

            {/* Mobile menu toggle */}
            <button
              ref={menuButtonRef}
              type="button"
              aria-expanded={open}
              aria-controls="page-header-mobile-navigation"
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="page-header-mobile-menu-toggle"
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setOpen((value) => !value)}
            >
              {open ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </>
        )}
      </div>

      {/* Mobile navigation */}
      {!isCalculatorPage && (
        <AnimatePresence>
          {open && (
            <motion.div
              id="page-header-mobile-navigation"
              ref={mobileMenuRef}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-gray-200"
            >
              <nav
                className="px-4 sm:px-6 py-4 flex flex-col gap-1"
                aria-label="Mobile navigation"
              >
                {NAV_LINKS.map((link, index) => {
                  const isActive =
                    link.href === "/"
                      ? pathname === "/"
                      : pathname === link.href ||
                        pathname.startsWith(`${link.href}/`);

                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setOpen(false)}
                      ref={index === 0 ? firstFocusableRef : null}
                      data-testid={`page-header-mobile-nav-${link.label
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                      className={`py-3 px-2 text-sm font-medium rounded-lg transition-colors ${
                        isActive
                          ? "bg-blue-50 text-[#0F2D52] font-semibold"
                          : "text-gray-800 hover:bg-gray-50"
                      }`}
                    >
                      {link.label}
                    </Link>
                  );
                })}

                {/* Keeps the focus trap's last element after the final link. */}
                <button
                  ref={lastFocusableRef}
                  type="button"
                  tabIndex={-1}
                  aria-hidden="true"
                  className="sr-only"
                />
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </header>
  );
}
